/**
 * The view `AuthenticatedRoute` should render for the current auth state and
 * location. A discriminated union so "what to show" is always one
 * unambiguous value instead of several booleans/branches that could
 * disagree (react-best-practices Rule 8).
 */
export type AuthRouteView =
  | { view: 'loading' }
  | { view: 'redirect'; to: string; replace?: boolean }
  | { view: 'content' };

export interface GetAuthRouteViewParams {
  isInitializing: boolean;
  /**
   * The single "is this user logged in" signal — see
   * `AuthProviderContextState.isAuthenticated`. Deliberately NOT
   * `sessions.olSession`: that reflects only the live olserver socket, which
   * `useGracefulLogout` intentionally lets go falsy for a reconnect grace
   * period without the user being logged out (CLASSIC-10283). Gating this
   * route on `sessions.olSession` again would silently reintroduce that bug
   * by racing `AuthenticatedRoute`'s redirect against the grace period.
   */
  isAuthenticated: boolean;
  pathname: string;
  loginPath: string;
  mapPath: string;
}

/**
 * Pure decision behind `AuthenticatedRoute`. Extracted so the redirect logic
 * is unit-testable without mounting the full provider tree in `app.tsx`.
 */
export function getAuthRouteView({
  isInitializing,
  isAuthenticated,
  pathname,
  loginPath,
  mapPath,
}: GetAuthRouteViewParams): AuthRouteView {
  // Still restoring a saved session on page load — don't redirect either way yet.
  if (isInitializing) {
    return { view: 'loading' };
  }

  if (!isAuthenticated) {
    return { view: 'redirect', to: loginPath };
  }

  // Logged in and sitting at the root path — send them straight to the map.
  if (pathname === '/') {
    return { view: 'redirect', to: mapPath, replace: true };
  }

  return { view: 'content' };
}
